import { useConversationCallStore } from "%/apps/video-engine/stores/conversation-call-store";

const CONVERSATION_ENTITY_TYPE = "Conversation";

// A call element is a host element, not an app one: it needs only a
// Conversation context, bound like any other (options.context). If that
// binding points at a context the component does not declare, or at one of
// another type, say so on the element instead of rendering a silent idle call.
function conversationCallBindingProblems({ element, dynamicComponent }) {
  const contextName = element.parsedOptions.context?.entityContext;
  if (!contextName) {
    return ["Not bound to a Conversation context. Bind it to the conversation this call belongs to."];
  }
  const declared = (dynamicComponent?.availableContextsEntities ?? []).find(entry => entry?.name === contextName);
  if (!declared) {
    return [
      `Bound to the context "${contextName}", which this ${dynamicComponent?.type ?? "component"} `
      + "does not provide. Re-point the binding to a Conversation context, or remove this element.",
    ];
  }
  const entityType = String(declared.entityType || "").split(".").at(-1);
  if (entityType && entityType !== CONVERSATION_ENTITY_TYPE) {
    return [`Bound to "${contextName}", a ${entityType}. A call element needs a Conversation context.`];
  }

  return [];
}

// Shared by Call Status, Call Participant Count, Join Call and Video Call.
// Turns the element's Conversation binding plus the render surface's
// installed-app payload into the call it reads or joins. The builder has no
// tenant: there every call element renders a placeholder and never touches the
// presence poll, the microphone or LiveKit.
export const ConversationCallElementMixin = {
  configurationProblems: conversationCallBindingProblems,
  inject: {
    inBuilderEditScope: {
      from: "inBuilderEditScope",
      default: false,
    },
  },
  computed: {
    callStore() {
      return useConversationCallStore();
    },
    isCallPlaceholder() {
      return Boolean(this.editMode || this.inBuilderEditScope || this.isPreviewMode);
    },
    callTarget() {
      if (this.isCallPlaceholder || typeof window === "undefined") {
        return null;
      }
      const data = window.blclient?.data ?? {};
      const appName = this.elementContext?.appName || "Chat";
      const target = {
        destinationId: Number(data.renderDestinationId || 0),
        configurationId: Number(data.dbApps?.[appName]?.configurationId || 0),
        conversationId: Number(this.contextItem?.entityPkUrlVars?.id || 0),
      };
      if (!target.destinationId || !target.configurationId || !target.conversationId) {
        return null;
      }

      return target;
    },
    // A stable identity for the watcher: callTarget is a fresh object per read.
    callTargetKey() {
      const target = this.callTarget;

      return target ? `${target.destinationId}:${target.configurationId}:${target.conversationId}` : "";
    },
    callPresence() {
      return this.callTarget ? this.callStore.presenceFor(this.callTarget) : { active: false, count: 0 };
    },
  },
  watch: {
    callTargetKey: {
      immediate: true,
      handler(key, previousKey) {
        if (previousKey && this.subscribedCallTarget) {
          this.callStore.unsubscribePresence(this.subscribedCallTarget);
          this.subscribedCallTarget = null;
        }
        if (key) {
          this.subscribedCallTarget = this.callTarget;
          this.callStore.subscribePresence(this.subscribedCallTarget);
        }
      },
    },
  },
  beforeUnmount() {
    if (this.subscribedCallTarget) {
      this.callStore.unsubscribePresence(this.subscribedCallTarget);
    }
  },
};
